/*
 * The clock the interface reads. Three surfaces show a time that ages while somebody looks at it —
 * a profile's local time, a thread's "last reply 2 minutes ago", an Activity row — and each of them
 * used to be free to invent its own interval, or to render a stamp that quietly went stale.
 *
 * One hook, one rule: the tick lands on the interval boundary rather than an interval after the
 * component mounted, so every stamp on the screen moves in the same moment and a minute clock never
 * shows a minute that is already over. A subscriber holds one timer and clears it on unmount.
 */

import { useEffect, useState } from "react"

/**
 * The current time in milliseconds, re-rendered on every boundary of `intervalMs` (a minute by
 * default). An interval that is not a positive number means "read the clock once and never again",
 * which is what a test that wants a fixed stamp asks for.
 */
export function useNow(intervalMs = 60_000): number {
	const [now, setNow] = useState(() => Date.now())
	useEffect(() => {
		if (!Number.isFinite(intervalMs) || intervalMs <= 0) return undefined
		let timer: ReturnType<typeof setTimeout>
		// The delay to the next boundary, not the whole interval: a component that mounts at 12:30:59
		// sees 12:31 one second later, together with everything else on the screen.
		const untilBoundary = (): number => {
			const rest = Date.now() % intervalMs
			return rest === 0 ? intervalMs : intervalMs - rest
		}
		const tick = (): void => {
			setNow(Date.now())
			timer = setTimeout(tick, untilBoundary())
		}
		timer = setTimeout(tick, untilBoundary())
		return () => clearTimeout(timer)
	}, [intervalMs])
	return now
}
